import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { subscribeEmail } from "@/lib/api";

/**
 * Reusable email-capture form.
 * Props:
 *  - source: subscription source tag stored in DB
 *  - testIdPrefix: data-testid namespace
 *  - variant: "light" (default, on white) | "dark" (on dark sections)
 *  - headline / sub: optional copy override
 */
export default function EmailCaptureForm({
  source = "landing_inline",
  testIdPrefix = "email-capture",
  variant = "light",
  headline,
  sub,
}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");
  const [alreadyIn, setAlreadyIn] = useState(false);

  const dark = variant === "dark";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError("Please enter a valid email.");
      return;
    }
    setError("");
    setStatus("loading");
    try {
      const res = await subscribeEmail(email.trim(), source);
      setAlreadyIn(!!res.already_subscribed);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "Something went wrong. Try again.");
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className={`flex items-start gap-3 px-4 py-3 border ${dark ? "border-white/20 bg-white/5 text-white" : "border-[#002FA7]/20 bg-[#002FA7]/5 text-zinc-900"}`}
      >
        <CheckCircle2 className={`w-5 h-5 mt-0.5 shrink-0 ${dark ? "text-[#7CFFA8]" : "text-[#002FA7]"}`} />
        <div className="text-sm">
          <div className="font-medium">
            {alreadyIn ? "You're already on the list." : "You're in. Watch your inbox."}
          </div>
          <div className={`mt-0.5 ${dark ? "text-white/70" : "text-zinc-500"}`}>
            {alreadyIn
              ? "We'll only ping you for the launch and the occasional drop."
              : "We just sent a confirmation. We'll email you the moment we launch."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid={testIdPrefix} className="w-full">
      {headline && (
        <div className={`font-display font-extrabold text-2xl lg:text-3xl tracking-tight ${dark ? "text-white" : "text-zinc-900"}`}>
          {headline}
        </div>
      )}
      {sub && (
        <p className={`mt-2 text-sm ${dark ? "text-white/70" : "text-zinc-500"}`}>{sub}</p>
      )}
      <div className={`mt-4 flex flex-col sm:flex-row gap-2`}>
        <input
          type="email"
          placeholder="you@work-email.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
          data-testid={`${testIdPrefix}-input`}
          aria-label="Email address"
          className={`flex-1 px-4 py-3 text-sm border outline-none transition-colors ${
            dark
              ? "bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-white"
              : "bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-[#002FA7]"
          }`}
        />
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid={`${testIdPrefix}-submit`}
          className={`inline-flex items-center justify-center gap-1.5 px-5 py-3 text-sm font-medium transition-colors disabled:opacity-60 ${
            dark
              ? "bg-[#EF4444] text-white hover:bg-white hover:text-zinc-900"
              : "bg-[#EF4444] text-white hover:bg-[#002FA7]"
          }`}
        >
          {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Notify me <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
      {error && (
        <div
          data-testid={`${testIdPrefix}-error`}
          className={`mt-2 text-xs ${dark ? "text-[#FCA5A5]" : "text-[#EF4444]"}`}
        >
          {error}
        </div>
      )}
      <p className={`mt-2 text-[11px] ${dark ? "text-white/40" : "text-zinc-400"}`}>
        No spam. Unsubscribe anytime. By subscribing you agree to our{" "}
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className={`underline ${dark ? "hover:text-white" : "hover:text-zinc-900"}`}>Privacy Policy</a>.
      </p>
    </form>
  );
}
